import { toast } from "react-toastify";
import { detect } from "detect-browser";
import * as Sentry from "@sentry/react";

const generate = require("project-name-generator");

export function generateProjectName() {
  let name = generate({}).spaced;

  const capitalized = name
    .split(" ")
    .map((n) => n[0].toUpperCase() + n.slice(1))
    .join(" ");
  return capitalized;
}

export function displayError(msg) {
  toast.error(msg, {
    position: "top-center",
    // autoClose: 5000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: false,
    progress: undefined,
    className: "text-center",
    style: { background: "rgba(194, 34, 50, .95)" },
  });
}
export function displayInfo(msg) {
  toast.info(msg, {
    position: "top-center",
    autoClose: 5000,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: false,
    progress: undefined,
    className: "text-center",
    style: { background: "rgba(34,194,97,0.95)" },
  });
}

export function formatTime(durationSeconds: number): string {
  // converts seconds to mm:ss format
  const minutes = Math.floor(durationSeconds / 60);
  const seconds = Math.round(durationSeconds % 60);

  return `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;
}

export function remainingDownload(
  downloadLengthLimit: number,
  currentDownloadLength: number,
  paidCredits: number
) {
  if (currentDownloadLength > downloadLengthLimit && !paidCredits) {
    // handle new pricing changes. Downloaded length might be more that new limit when new pricing changes were implemented
    return {
      seconds: 0,
      message: "0 minutes",
      formattedTime: "00:00",
    };
  }

  let remainingDownloadInSeconds = 0;

  if (downloadLengthLimit) {
    remainingDownloadInSeconds = downloadLengthLimit - currentDownloadLength;
  }

  if (paidCredits) {
    remainingDownloadInSeconds += paidCredits ? paidCredits : 0;
  }

  const remainingDownloadInMinutes = Math.floor(
    remainingDownloadInSeconds / 60
  );
  const formattedTime = formatTime(remainingDownloadInSeconds);
  if (remainingDownloadInMinutes == 0) {
    return {
      seconds: remainingDownloadInSeconds,
      message: "less than 1 minute",
      formattedTime,
    };
  } else if (remainingDownloadInMinutes == 1) {
    return {
      seconds: remainingDownloadInSeconds,
      message: "1 minute",
      formattedTime,
    };
  } else {
    return {
      seconds: remainingDownloadInSeconds,
      message: `${remainingDownloadInMinutes} minutes`,
      formattedTime,
    };
  }
}

export function sharedArrayBufferSupported() {
  const supportedMinVersions = {
    chrome: "96.0",
    //safari: "15.2",
    //firefox: "79.0",
  };

  const browser = detect();
  const [maj, min] = browser.version.split(".").map((n) => Number(n));

  const minVersion = supportedMinVersions[browser.name.toLowerCase()];
  if (minVersion) {
    const [targetMaj, targetMin] = minVersion.split(".").map((n) => Number(n));

    if (maj >= targetMaj && min >= targetMin) {
      return true;
    } else {
      return false;
    }
  } else {
    return false;
  }
}

export function isBlobUrl(url) {
  return url.startsWith("blob:");
}

export function getUniqueTeamMembersCount(teamData, totalMembers) {
  const uniqueMembers = new Set();
  teamData.forEach((team) => {
    team.members.forEach((member) => {
      if (member.id !== totalMembers) {
        uniqueMembers.add(member.id);
      }
    });
  });
  return uniqueMembers.size;
}

export function extractUrlType(url) {
  const urlPatterns = {
    youtube:
      /^((?:https?:)?\/\/)?((?:www|m)\.)?((?:youtube(-nocookie)?\.com|youtu.be))(\/(?:[\w\-]+\?v=|embed\/|v\/)?)([\w\-]+)(\S+)?$/gi,
    vimeo: /vimeo\.com\/channels\/(?:\w+\/)?/gi,
    instagram:
      /(?:(?:http|https):\/\/)?(?:www.)?(?:instagram.com|instagr.am|instagr.com)\/(\w+)/gi,
    other:
      /[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)?/gi,
  };

  let matchedType;

  Object.keys(urlPatterns).forEach((urlType) => {
    const regex = new RegExp(urlPatterns[urlType]);
    if (url.match(regex) && !matchedType) {
      matchedType = urlType;
    }
  });
  return matchedType;
}

export function debounceAsync(func, delay) {
  let timeoutId;

  return async function () {
    const context = this;
    const args = arguments;

    // Clear the previous timeout
    clearTimeout(timeoutId);

    // Set a new timeout
    timeoutId = setTimeout(async () => {
      await func.apply(context, args);
    }, delay);
  };
}

export function debounceSync(func, wait, immediate) {
  let timeout;

  return function executedFunction(...args) {
    let later = function () {
      timeout = null;
      if (!immediate) func(...args);
    };

    let callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);

    if (callNow) func(...args);
  };
}
export const fetcher = (...args) =>
  fetch(...args)
    .then((res) => res.json())
    .catch((error) => {
      Sentry.captureException(error, {
        extra: { fetchArgs: args },
        tags: { fetcher: "fetcher" },
      });
    });

export const fetcherWithUser = (...args) =>
  fetch(...args)
    .then((res) => res.text())
    .then((text) => {
      if (text === "User not found") {
        return {
          tos_accept: false,
          user_questions: false,
          projects: [],
        };
      } else {
        try {
          return JSON.parse(text);
        } catch (error) {
          Sentry.captureException(error, {
            extra: { fetchArgs: args, responseText: text },
            tags: { fetcher: "fetcherWithUser" },
          });
          // fallback
          return {
            tos_accept: false,
            user_questions: false,
            projects: [],
          };
        }
      }
    })
    .catch((error) => {
      Sentry.captureException(error, {
        extra: { fetchArgs: args },
        tags: { fetcher: "fetcherWithUser" },
      });
    });
